import {
  ChevronDown,
  ChevronRight,
  Database,
  Info,
  Lock,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import React, { useState } from "react";
import { useApp } from "../../contexts/AppContext";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import { useFormatting } from "../../hooks/useFormatting";
import { useTerminology } from "../../hooks/useTerminology";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

interface BaseForecastCardProps {
  onView: () => void;
  onCompare: () => void;
  onClone: () => void;
}

export const BaseForecastCard: React.FC<BaseForecastCardProps> = () => {
  const { getForecastTerm } = useTerminology();
  const { formatCurrency } = useFormatting();
  const { mode } = useApp();
  const [expanded, setExpanded] = useState(true);

  const { baseForecast: forecastData, isLoading } = useBaseForecast();
  const currentBalance = forecastData[0]?.balance ?? 0;
  const day90Balance = forecastData[forecastData.length - 1]?.balance ?? 0;
  const netChange = day90Balance - currentBalance;
  const minBalance = Math.min(...forecastData.map((d) => d.balance));

  const forecastTerm = getForecastTerm(true);

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
        Loading base {forecastTerm.toLowerCase()}…
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
        {/* Header row — always visible, click to toggle */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-100 transition-colors"
        >
          {/* Collapse chevron */}
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
          )}

          {/* Section label */}
          <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide shrink-0">
            Ground Truth — Base {forecastTerm}
          </span>

          {/* Identity badges */}
          <div className="flex items-center gap-2 shrink-0">
            <Badge className="bg-slate-700 text-white hover:bg-slate-700 font-medium text-xs gap-1">
              <Lock className="h-3 w-3" />
              Base {forecastTerm}
            </Badge>
            <Badge
              variant="outline"
              className="bg-white text-slate-600 border-slate-300 text-xs gap-1"
            >
              <Database className="h-3 w-3" />
              {mode === "demo" ? "Access Financials" : "Live Data"}
            </Badge>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Always Active badge — always visible even when collapsed */}
          <Badge
            variant="outline"
            className="text-xs bg-white border-slate-300 text-slate-500 shrink-0"
          >
            Always Active
          </Badge>
        </button>

        {/* Expandable detail row */}
        {expanded && (
          <div className="flex items-center gap-4 px-4 pb-3 flex-wrap border-t border-slate-200">
            {/* Net change pill */}
            <div className="flex items-center gap-1.5 shrink-0 mt-3">
              {netChange >= 0 ? (
                <TrendingUp className="h-4 w-4 text-slate-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-slate-500" />
              )}
              <span className="text-sm text-slate-600">
                {netChange >= 0
                  ? `+${formatCurrency(netChange, "full")}`
                  : formatCurrency(netChange, "full")}
                <span className="text-slate-400 ml-1">over 90 days</span>
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-slate-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  Derived from your connected data sources. Edit your data in
                  Access Financials — this updates automatically.
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-5 bg-slate-200 shrink-0 mt-3" />

            {/* Metrics row */}
            <div className="flex items-center gap-5 mt-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400">Current</span>
                <span className="text-sm font-semibold text-slate-800">
                  {formatCurrency(currentBalance, "full")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400">Day 90</span>
                <span className="text-sm font-semibold text-slate-800">
                  {formatCurrency(day90Balance, "full")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400">Min Cash</span>
                <span
                  className={cn(
                    "text-sm font-semibold",
                    minBalance < 50000
                      ? "text-red-600"
                      : minBalance < 100000
                        ? "text-amber-600"
                        : "text-slate-800",
                  )}
                >
                  {formatCurrency(minBalance, "full")}
                </span>
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Read-only note */}
            <span className="text-xs text-slate-400 italic hidden md:block mt-3">
              Read-only — cannot be edited, parked, or deleted
            </span>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
